import dayjs from "dayjs";

export const RESERVATION_STATUS = {
  PENDING: "pending",
  RELEASED: "released",
  CANCELLED: "cancelled",
  NO_SHOW: "no_show",
};

export const reservationStatusText = (status) => ({
  [RESERVATION_STATUS.PENDING]: "待處理",
  [RESERVATION_STATUS.RELEASED]: "已開始製作",
  [RESERVATION_STATUS.CANCELLED]: "已取消",
  [RESERVATION_STATUS.NO_SHOW]: "未到店",
}[status] || status || "-");

export const reservationStatusColor = (status) => ({
  [RESERVATION_STATUS.PENDING]: { background: "#FFF3E0", color: "#A34B00" },
  [RESERVATION_STATUS.RELEASED]: { background: "#E8F5E9", color: "#1B5E20" },
  [RESERVATION_STATUS.CANCELLED]: { background: "#FCE8E6", color: "#B3261E" },
  [RESERVATION_STATUS.NO_SHOW]: { background: "#ECEFF1", color: "#455A64" },
}[status] || { background: "#ECEFF1", color: "#455A64" });

export const reservationItemCount = (order) => (order?.buycart || []).reduce(
  (total, item) => total + Number(item?.qty || 0),
  0
);

export const defaultReservationTime = () => {
  const value = dayjs().add(1, "hour").second(0).millisecond(0);
  const roundedMinute = Math.ceil(value.minute() / 15) * 15;
  return value.minute(0).add(roundedMinute, "minute").format("YYYY-MM-DDTHH:mm");
};

const MINUTE_MS = 60 * 1000;
const OVERDUE_AFTER_MS = 15 * MINUTE_MS;
// 送單前多久先提醒、到送單時間後沒人按「開始製作」每隔多久再提醒
export const SEND_SOON_MS = 10 * MINUTE_MS;
export const REMIND_EVERY_MS = 5 * MINUTE_MS;

// 預計送到出餐管理的時間（ms）：後端 reservation_release_at 為準，沒有就退回預約前 30 分鐘
export const reservationSendAt = (order) => {
  const reservationAt = dayjs(order?.get_time).valueOf();
  if (!Number.isFinite(reservationAt)) return null;
  return order?.reservation_release_at
    ? dayjs(order.reservation_release_at).valueOf()
    : reservationAt - 30 * MINUTE_MS;
};

export const reservationTiming = (order, nowMs = Date.now()) => {
  if (order?.reservation_status !== RESERVATION_STATUS.PENDING || !order?.get_time) {
    return "normal";
  }

  const reservationAt = dayjs(order.get_time).valueOf();
  if (!Number.isFinite(reservationAt)) return "normal";
  if (nowMs > reservationAt + OVERDUE_AFTER_MS) return "overdue";

  return nowMs >= reservationSendAt(order) ? "due" : "upcoming";
};

export const reservationTimingText = (order, nowMs = Date.now()) => {
  const timing = reservationTiming(order, nowMs);
  if (timing === "overdue") return "已超過預約時間";
  if (timing === "due") return "可開始準備";
  if (timing === "upcoming") {
    const minutes = Math.max(1, dayjs(order.get_time).diff(dayjs(nowMs), "minute"));
    return `${minutes} 分鐘後`;
  }
  return "";
};

// 預約／預訂單預計送到出餐管理的提示文字（時間與提前量都以後端 reservation_release_at / kitchen_lead_minutes 為準）
export const kitchenSendText = (order) => {
  if (!order?.get_time) return "";
  if (!reservationItemCount(order)) return "點餐後依金額提前 20～60 分鐘送到出餐管理";
  if (!order.reservation_release_at || !order.kitchen_lead_minutes) return "";
  return `預計 ${dayjs(order.reservation_release_at).format("HH:mm")} 送到出餐管理（提前 ${order.kitchen_lead_minutes} 分鐘）`;
};

// 首頁卡片／出餐看板用的短標籤：重點是「什麼時候要送出餐」而不是離客人到店多久
export const reservationSendText = (order, nowMs = Date.now()) => {
  const timing = reservationTiming(order, nowMs);
  if (timing === "overdue") return "已超過預約時間";
  if (!reservationItemCount(order)) return "到店點餐";
  if (timing === "due") return "該送出餐了";
  if (timing === "upcoming") {
    const sendAt = reservationSendAt(order);
    const minutes = Math.max(1, Math.ceil((sendAt - nowMs) / MINUTE_MS));
    return `${dayjs(sendAt).format("HH:mm")} 送出餐（${minutes} 分後）`;
  }
  return "";
};

const NOTIFY_TIMINGS = ["due", "overdue"];

// 今日待處理預約中，已到可準備時間或已逾時的筆數（首頁徽章／卡片標題用）
export const dueReservationCount = (reservations = [], nowMs = Date.now()) =>
  reservations.filter((order) => NOTIFY_TIMINGS.includes(reservationTiming(order, nowMs))).length;

// 提醒階段：送單前 10 分 soon → 到送單時間 due → 超過預約 15 分 overdue
// 沒預點的預約（到店才點）沒有東西要送出餐，不做 soon 預告
export const reservationReminderStage = (order, nowMs = Date.now()) => {
  const timing = reservationTiming(order, nowMs);
  if (timing === "due" || timing === "overdue") return timing;
  if (timing === "upcoming" && reservationItemCount(order) && nowMs >= reservationSendAt(order) - SEND_SOON_MS) {
    return "soon";
  }
  return null;
};

// 從清單挑出需要提醒的預約。notifiedKeys 去重：soon 只提醒一次；有預點的 due/overdue
// 每 5 分鐘一輪（key 帶輪次），直到按「開始製作」／取消離開待處理清單為止；沒預點的各提醒一次。
export const pickReservationsToNotify = (reservations = [], nowMs = Date.now(), notifiedKeys = new Set()) => {
  const picked = [];
  reservations.forEach((order) => {
    const timing = reservationReminderStage(order, nowMs);
    if (!timing) return;
    let key = `${order.order_id}:${timing}`;
    if (timing !== "soon" && reservationItemCount(order)) {
      const stageStart = timing === "due"
        ? reservationSendAt(order)
        : dayjs(order.get_time).valueOf() + OVERDUE_AFTER_MS;
      key += `:${Math.max(0, Math.floor((nowMs - stageStart) / REMIND_EVERY_MS))}`;
    }
    if (notifiedKeys.has(key)) return;
    notifiedKeys.add(key);
    picked.push({ order, timing });
  });
  return picked;
};

export const reservationNotifyMessage = (order, timing) => {
  const who = `${order?.name || "客人"} ${dayjs(order?.get_time).format("HH:mm")} 的預約`;
  const noItems = !reservationItemCount(order);
  if (timing === "overdue") return `${who}已超過時間`;
  if (timing === "soon") {
    return `${who}將在 ${dayjs(reservationSendAt(order)).format("HH:mm")} 送出餐`;
  }
  return noItems ? `${who}快到了（到店點餐）` : `${who}該送出餐了，請按「開始製作」`;
};
